"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.roundHalfUp2 = roundHalfUp2;
exports.subtractMoney = subtractMoney;
exports.isNegativeAmount = isNegativeAmount;
exports.computeInvoiceTotals = computeInvoiceTotals;
const decimal_js_1 = __importDefault(require("decimal.js"));
/**
 * All money and quantity values travel as decimal STRINGS (e.g. "1234.56").
 * Arithmetic is done with decimal.js to avoid floating-point error — there is
 * no `parseFloat`/`Number` anywhere on a money path in this file, by design:
 * a single float64 round-trip is enough to turn 2.345 into 2.34 and put a
 * legally-wrong VAT amount on an invoice.
 *
 * Rounding is ROUND_HALF_UP (decimal.js rounds a tie AWAY from zero), which
 * is both the Romanian/EN 16931 convention and what makes a storno an EXACT
 * mirror: round2(-x) === -round2(x) for every x, so a credit note's totals
 * are the original's totals with the sign flipped, never a cent off.
 */
// A module-private decimal.js constructor, so configuring precision/rounding
// here can never leak into (or be clobbered by) any other decimal.js user in
// the process — notably Prisma's own bundled Decimal. 40 significant digits
// is far beyond what a 14,2 money column and a 12,3 quantity column can
// produce when multiplied.
const Decimal = decimal_js_1.default.clone({ precision: 40, rounding: decimal_js_1.default.ROUND_HALF_UP });
const SCALE = 2;
const HUNDRED = new Decimal(100);
/**
 * Rounds to exactly 2 decimals, half-up (ties away from zero), and formats
 * the result as a plain decimal string with both decimals always present
 * ("5" -> "5.00"). Negative zero is normalized to "0.00": decimal.js keeps
 * the sign of a zero, so `round2(-1 × 0)` would otherwise render "-0.00",
 * which is a meaningless amount on an invoice and would break the storno
 * mirror (the mirror of "0.00" is "0.00", not "-0.00").
 */
function roundHalfUp2(value) {
    const rounded = new Decimal(value).toDecimalPlaces(SCALE, decimal_js_1.default.ROUND_HALF_UP);
    if (rounded.isZero()) {
        return new Decimal(0).toFixed(SCALE);
    }
    return rounded.toFixed(SCALE);
}
/** Internal: same rounding, kept as a Decimal so callers can keep summing
 * without a string round-trip. */
function round2(value) {
    return new Decimal(value).toDecimalPlaces(SCALE, decimal_js_1.default.ROUND_HALF_UP);
}
/** Formats an already-2dp Decimal, normalizing -0 the same way
 * `roundHalfUp2` does. */
function format2(value) {
    return value.isZero() ? new Decimal(0).toFixed(SCALE) : value.toFixed(SCALE);
}
/**
 * Exact 2dp difference `a - b`, for the one money subtraction this app does
 * outside `computeInvoiceTotals`: "rest de încasat" = total − paid (invoice
 * list, invoice view, the payment modal's prefilled amount). It lives here,
 * next to the rest of the money math, for exactly the reason stated at the
 * top of this file — the alternative on the frontend would have been
 * `Number(total) - Number(paid)`, i.e. a float64 round-trip on a money path.
 *
 * Both inputs are already-2dp decimal strings as they come off the wire, so
 * the subtraction is exact and `round2` only normalizes the scale (and -0).
 */
function subtractMoney(a, b) {
    return format2(round2(new Decimal(a).minus(b)));
}
/** True when `value` is a negative money/quantity string — the sign test used
 * for display decisions (a negative total is a storno and is rendered red).
 * A string test rather than `Number(value) < 0` keeps money off the float
 * path even for a comparison. "-0.00" is not negative. */
function isNegativeAmount(value) {
    return value.trim().startsWith('-') && !new Decimal(value).isZero();
}
/** A group's identity is the (category code, rate) pair — two lines at the
 * same rate under different codes are legally different boxes on the
 * invoice, and so are two lines under the same code at different rates. */
function groupKey(vatCategoryCode, vatRatePercent) {
    // Normalize the rate so "21" and "21.00" land in the same group.
    return `${vatCategoryCode}|${new Decimal(vatRatePercent).toFixed(SCALE)}`;
}
/**
 * The single source of truth for every amount on an invoice — used
 * identically for drafts (indicative rates) and at issue time (resolved
 * rates), so what a user previewed is exactly what gets frozen.
 *
 * Per line: `lineNet = round2(quantity × unitPrice)`, then `lineVat =
 * round2(lineNet × rate/100)` and `lineTotal = round2(lineNet + lineVat)`.
 * The per-line VAT is DISPLAY ONLY — it is deliberately NOT what the invoice
 * totals are built from.
 *
 * Per VAT group: `taxableAmount = Σ lineNet` (a sum of values that are each
 * already exactly 2dp, so the sum is exact) and `vatAmount =
 * round2(taxableAmount × rate/100)`. This is EN 16931 BR-CO-17: the VAT of a
 * category is computed ONCE, on the category's taxable total — not as the
 * sum of the per-line VATs, which can differ by a cent or more once several
 * lines each round in the same direction.
 *
 * Invoice: `subtotal = Σ lineNet`, `vatTotal = Σ group vatAmount`, `total =
 * subtotal + vatTotal`. Negative quantities flow through unchanged and yield
 * negative amounts throughout — that is exactly how a storno is computed.
 *
 * Breakdown groups come back in first-appearance order (the order their
 * first line occupies on the invoice), so the rendered VAT table is stable
 * and matches the order a reader scans the lines in.
 */
function computeInvoiceTotals(lines) {
    if (lines.length === 0) {
        throw new Error('EMPTY_INVOICE');
    }
    const computedLines = [];
    // Map preserves insertion order, which is what gives the breakdown its
    // first-appearance ordering.
    const groups = new Map();
    let subtotal = new Decimal(0);
    for (const input of lines) {
        const rate = new Decimal(input.vatRatePercent);
        const lineNet = round2(new Decimal(input.quantity).times(input.unitPrice));
        const lineVat = round2(lineNet.times(rate).dividedBy(HUNDRED));
        const lineTotal = round2(lineNet.plus(lineVat));
        computedLines.push({
            ...input,
            lineNet: format2(lineNet),
            lineVat: format2(lineVat),
            lineTotal: format2(lineTotal),
        });
        subtotal = subtotal.plus(lineNet);
        const key = groupKey(input.vatCategoryCode, input.vatRatePercent);
        const existing = groups.get(key);
        if (existing) {
            existing.taxable = existing.taxable.plus(lineNet);
        }
        else {
            groups.set(key, {
                vatCategoryCode: input.vatCategoryCode,
                vatRatePercent: rate.toFixed(SCALE),
                taxable: lineNet,
            });
        }
    }
    const breakdown = [];
    let vatTotal = new Decimal(0);
    for (const group of groups.values()) {
        const vatAmount = round2(group.taxable.times(group.vatRatePercent).dividedBy(HUNDRED));
        vatTotal = vatTotal.plus(vatAmount);
        breakdown.push({
            vatCategoryCode: group.vatCategoryCode,
            vatRatePercent: group.vatRatePercent,
            taxableAmount: format2(group.taxable),
            vatAmount: format2(vatAmount),
        });
    }
    return {
        lines: computedLines,
        breakdown,
        subtotal: format2(subtotal),
        vatTotal: format2(vatTotal),
        total: format2(subtotal.plus(vatTotal)),
    };
}
